<template>
  <div class="pm-tab-shell h-100">

    <!-- Modal: Create / Edit Test Plan -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-overlay">
        <div
          class="modal-box plan-modal-box"
          :class="{ 'plan-modal-box--split': editId }"
          :style="editId ? 'width:1220px;' : 'width:520px;'"
        >
          <div class="d-flex align-items-center p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">{{ editId ? 'Edit Test Plan' : 'Create Test Plan' }}</h6>
          </div>
          <div :class="editId ? 'plan-modal-split-body' : ''">
            <div class="p-3" :class="editId ? 'plan-modal-pane-form' : ''">
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Sprint</label>
              <div class="position-relative" ref="sprintDropdownRef">
                <div
                  class="form-control form-control-sm d-flex flex-wrap gap-1 align-items-center"
                  style="min-height:31px;cursor:pointer;"
                  @click="showSprintDropdown = !showSprintDropdown"
                >
                  <span v-if="form.sprintIds.length === 0" class="text-secondary text-xs">— Select sprint —</span>
                  <span
                    v-for="sid in form.sprintIds"
                    :key="sid"
                    class="badge badge-sm me-1"
                    style="background:#6f42c1;color:#fff;font-size:0.7rem;"
                  >
                    {{ sprintShortLabelById(sid) }}
                    <span class="ms-1" style="cursor:pointer;" @click.stop="removeSprint(sid)">×</span>
                  </span>
                </div>
                <div
                  v-if="showSprintDropdown"
                  class="border rounded bg-white shadow-sm position-absolute w-100"
                  style="z-index:9999;max-height:200px;overflow-y:auto;top:100%;"
                >
                  <div
                    v-for="s in availableSprintsForForm"
                    :key="s.sprintId"
                    class="px-3 py-2 d-flex align-items-center gap-2"
                    style="cursor:pointer;font-size:0.8rem;"
                    :style="form.sprintIds.includes(Number(s.sprintId)) ? 'background:#f3efff;' : ''"
                    @click="toggleSprint(Number(s.sprintId))"
                  >
                    <input
                      class="bo-checkbox-input"
                      type="checkbox"
                      :checked="form.sprintIds.includes(Number(s.sprintId))"
                      readonly
                    />
                    <span>{{ sprintOptionLabel(s) }}</span>
                  </div>
                  <div v-if="!availableSprintsForForm.length" class="px-3 py-2 text-secondary text-xs">
                    No sprints for this team
                  </div>
                </div>
              </div>
            </div>
            <div v-if="!editId">
              <!--
                Switched from native <select> to <base-select> so the open
                dropdown menu visually matches the design system used on
                the Management page (white panel, rounded rows, purple
                focus ring) rather than the OS-native dark popup that
                native <select> renders.
              -->
              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Project <span class="text-danger">*</span></label>
                <base-select
                  size="sm"
                  :model-value="form.projectId === '' ? null : Number(form.projectId)"
                  :options="projects"
                  option-label="projectName"
                  option-value="projectId"
                  placeholder="— Select project —"
                  @update:model-value="(v) => { form.projectId = v == null ? '' : Number(v); onProjectChange(); }"
                />
              </div>
              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Sub Project <span class="text-danger">*</span></label>
                <base-select
                  size="sm"
                  :model-value="form.subProjectId === '' ? null : Number(form.subProjectId)"
                  :options="filteredSubProjects"
                  option-label="subProjectName"
                  option-value="subProjectId"
                  :disabled="!form.projectId"
                  placeholder="— Select sub project —"
                  @update:model-value="(v) => { form.subProjectId = v == null ? '' : Number(v); onSubProjectChange(); }"
                >
                  <template #option="{ option }">
                    <span>{{ option.subProjectName }}</span>
                    <span v-if="option.disabled" class="text-secondary ms-1" style="font-size:0.65rem;">(already in plan)</span>
                  </template>
                </base-select>
              </div>
            </div>
            <div class="row">
              <div class="col-6 mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Start Date</label>
                <input type="date" class="form-control form-control-sm" v-model="form.planOverallStart" />
              </div>
              <div class="col-6 mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Due Date</label>
                <input type="date" class="form-control form-control-sm" v-model="form.planOverallDue" />
              </div>
            </div>

            <!-- QA Owner multi-select dropdown -->
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">QA Owner</label>
              <div class="position-relative" ref="qaDropdownRef">
                <div
                  class="form-control form-control-sm d-flex flex-wrap gap-1 align-items-center"
                  style="min-height:31px;cursor:pointer;"
                  @click="showQaDropdown = !showQaDropdown"
                >
                  <span v-if="form.qaOwnerIds.length === 0" class="text-secondary text-xs">— Select QA Owner —</span>
                  <span
                    v-for="uid in form.qaOwnerIds"
                    :key="uid"
                    class="badge badge-sm me-1"
                    style="background:#17c1e8;color:#fff;font-size:0.7rem;"
                  >
                    {{ userNameById(uid) }}
                    <span class="ms-1" style="cursor:pointer;" @click.stop="removeQaOwner(uid)">×</span>
                  </span>
                </div>
                <div v-if="showQaDropdown" class="border rounded bg-white shadow-sm position-absolute w-100" style="z-index:9999;max-height:180px;overflow-y:auto;top:100%;">
                  <div
                    v-for="u in users"
                    :key="u.userId"
                    class="px-3 py-2 d-flex align-items-center gap-2"
                    style="cursor:pointer;font-size:0.8rem;"
                    :style="form.qaOwnerIds.includes(Number(u.userId)) ? 'background:#f0f9ff;' : ''"
                    @click.stop="toggleQaOwner(Number(u.userId))"
                  >
                    <i
                      class="fas text-xs me-1"
                      :class="form.qaOwnerIds.includes(Number(u.userId)) ? 'fa-check-square text-info' : 'fa-square text-secondary'"
                    ></i>
                    {{ formatUserDisplayName(u) }}
                  </div>
                  <div v-if="users.length === 0" class="px-3 py-2 text-secondary text-xs">No users found</div>
                </div>
              </div>
            </div>

            <!-- Dev Owner multi-select dropdown -->
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Dev Owner</label>
              <div class="position-relative" ref="devDropdownRef">
                <div
                  class="form-control form-control-sm d-flex flex-wrap gap-1 align-items-center"
                  style="min-height:31px;cursor:pointer;"
                  @click="showDevDropdown = !showDevDropdown"
                >
                  <span v-if="form.devOwnerIds.length === 0" class="text-secondary text-xs">— Select Dev Owner —</span>
                  <span
                    v-for="uid in form.devOwnerIds"
                    :key="uid"
                    class="badge badge-sm me-1"
                    style="background:#82d616;color:#fff;font-size:0.7rem;"
                  >
                    {{ userNameById(uid) }}
                    <span class="ms-1" style="cursor:pointer;" @click.stop="removeDevOwner(uid)">×</span>
                  </span>
                </div>
                <div v-if="showDevDropdown" class="border rounded bg-white shadow-sm position-absolute w-100" style="z-index:9999;max-height:180px;overflow-y:auto;top:100%;">
                  <div
                    v-for="u in users"
                    :key="u.userId"
                    class="px-3 py-2 d-flex align-items-center gap-2"
                    style="cursor:pointer;font-size:0.8rem;"
                    :style="form.devOwnerIds.includes(Number(u.userId)) ? 'background:#f0fff0;' : ''"
                    @click.stop="toggleDevOwner(Number(u.userId))"
                  >
                    <i
                      class="fas text-xs me-1"
                      :class="form.devOwnerIds.includes(Number(u.userId)) ? 'fa-check-square text-success' : 'fa-square text-secondary'"
                    ></i>
                    {{ formatUserDisplayName(u) }}
                  </div>
                  <div v-if="users.length === 0" class="px-3 py-2 text-secondary text-xs">No users found</div>
                </div>
              </div>
            </div>
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">PM Owner</label>
              <base-select
                size="sm"
                :model-value="form.pmOwnerId === '' ? null : Number(form.pmOwnerId)"
                :options="users.map((u) => ({ value: Number(u.userId), label: formatUserDisplayName(u) }))"
                option-label="label"
                option-value="value"
                placeholder="— Select PM Owner —"
                @update:model-value="(v) => { form.pmOwnerId = v == null ? '' : Number(v); }"
              />
            </div>
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Scope</label>
              <textarea
                v-model="form.scope"
                class="form-control form-control-sm"
                rows="3"
                placeholder="Enter scope..."
                style="font-size:0.8rem; resize:vertical;"
              ></textarea>
            </div>

            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Status</label>
              <base-select
                size="sm"
                :model-value="form.status"
                :options="testPlanStatusOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select status —"
                @update:model-value="(v) => { form.status = v == null ? '' : v; }"
              />
            </div>
            <!-- Jira Space + Tickets -->
            <div>
              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Jira Space</label>
                <base-select
                  size="sm"
                  :model-value="form.jiraProjectKey || null"
                  :options="jiraProjects.map((p) => ({ value: p.key, label: `${p.name} (${p.key})` }))"
                  option-label="label"
                  option-value="value"
                  placeholder="— Select Space —"
                  @update:model-value="(v) => { form.jiraProjectKey = v == null ? '' : v; onJiraProjectChange(); }"
                />
                <span v-if="jiraLoadingProjects" class="text-xs text-secondary mt-1 d-block">
                  <i class="fas fa-spinner fa-spin me-1"></i>Loading spaces...
                </span>
              </div>
              <div v-if="form.jiraProjectKey" class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Jira Tickets <span class="text-secondary font-weight-normal">(optional)</span></label>
                <p v-if="editId" class="text-xs text-secondary mb-2">เลือก ticket จากรายการ — รายละเอียดและสถานะแสดงที่คอลัมน์ Jira ด้านขวา</p>
                <div v-if="!editId && form.jiraIssueKeys.length > 0" class="d-flex flex-wrap gap-1 mb-2">
                  <span v-for="key in form.jiraIssueKeys" :key="key" class="jira-tag">
                    <i class="fab fa-jira me-1"></i>{{ key }}
                    <button type="button" class="jira-tag-remove" @click="removeJiraTicket(key)">×</button>
                  </span>
                </div>
                <input type="text" class="form-control form-control-sm mb-1" v-model="jiraSearch"
                  placeholder="Search ticket..." @input="onJiraSearch" />
                <div class="jira-issue-list" :class="{ 'jira-issue-list--plain': editId }">
                  <div v-if="jiraLoadingIssues" class="text-center py-2 text-secondary text-xs">
                    <i class="fas fa-spinner fa-spin me-1"></i>Loading...
                  </div>
                  <div v-else-if="jiraIssues.length === 0" class="text-center py-2 text-secondary text-xs">No tickets found</div>
                  <div v-for="i in jiraIssues" :key="i.key"
                    class="jira-issue-item"
                    :class="{ 'jira-issue-selected': !editId && form.jiraIssueKeys.includes(i.key) }"
                    @click="toggleJiraTicket(i.key)">
                    <template v-if="!editId">
                      <i class="fas fa-check-circle me-1 text-success" v-if="form.jiraIssueKeys.includes(i.key)"></i>
                      <i class="fas fa-circle me-1" style="font-size:0.5rem; color:transparent;" v-else></i>
                    </template>
                    <span class="text-xs font-weight-bold me-1">{{ i.key }}</span>
                    <span class="text-xs">{{ i.summary }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="editId" class="plan-modal-pane-jira">
            <h6 class="text-sm font-weight-bold mb-2">
              <i class="fab fa-jira text-primary me-1"></i>Jira
            </h6>
            <jira-links-panel
              embedded
              :links="planJiraPanelLinks"
              :key="'plan-jira-' + (form.jiraIssueKeys || []).join(',')"
            />
          </div>
          </div>
          <div class="d-flex justify-content-end gap-2 p-3 border-top">
            <div v-if="saveError" class="me-auto text-danger text-xs align-self-center">{{ saveError }}</div>
            <button class="btn btn-sm btn-outline-secondary mb-0" :disabled="saving" @click="closeModal">Cancel</button>
            <button class="btn btn-sm bg-gradient-dark text-white mb-0" :disabled="saving" @click="save">
              <span v-if="saving"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
              <span v-else>Save</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <div class="plan-mgmt-inset">
    <!-- Toolbar: sprint filter + create -->
    <div class="pm-tab-toolbar d-flex justify-content-end mb-3">
      <div class="plan-table-toolbar-actions">
        <div v-if="isHistoryMode && !lockTeamFilter" class="sprint-list-filter">
          <label class="form-label text-xs text-secondary mb-1">Team</label>
          <base-select
            size="sm"
            :model-value="historyTeamFilter"
            :options="historyTeamFilterOptions"
            option-label="label"
            option-value="value"
            placeholder="All teams"
            @update:model-value="historyTeamFilter = $event"
          />
        </div>
        <div v-else-if="!isHistoryMode && !isBacklogMode && !isAssignMode && !isReadyMode" class="sprint-list-filter">
          <label class="form-label text-xs text-secondary mb-1">Sprint</label>
          <base-select
            size="sm"
            :model-value="listSprintFilter"
            :options="listSprintFilterOptions"
            option-label="label"
            option-value="value"
            placeholder="All"
            @update:model-value="listSprintFilter = $event"
          />
        </div>
        <button
          v-if="!isHistoryMode && !isReadyMode"
          class="btn btn-sm btn-outline-secondary mb-0 table-create-btn"
          @click="openCreate"
        >
          <i class="fas fa-plus me-1"></i> Create Plan
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="pm-tab-table-wrap">
      <div class="pm-table pm-tab-table">
        <div class="pm-table-header pm-tab-table-header">
          <span class="col-sub-project">SUB PROJECT</span>
          <span class="col-project">PROJECT</span>
          <span v-if="isHistoryMode" class="col-team text-center">TEAM</span>
          <span class="col-sprint text-center">SPRINT</span>
          <span class="col-date text-center">START DATE</span>
          <span class="col-date text-center">DUE DATE</span>
          <span class="col-status text-center">STATUS</span>
          <span v-if="!isHistoryMode" class="col-action text-center">ACTION</span>
        </div>
        <div class="pm-tab-table-body">
        <div v-if="loading" class="pm-empty-table-row">
          <i class="fas fa-spinner fa-spin fa-lg text-secondary"></i>
          <p class="text-secondary text-sm mt-2 mb-0">Loading...</p>
        </div>
        <div v-else-if="!displayedTestPlans.length" class="pm-empty-table-row">
          <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
          <p class="text-secondary text-sm mt-2 mb-0">{{ emptyPlansMessage }}</p>
        </div>
        <div v-for="tp in displayedTestPlans" :key="tp.testPlanId" class="pm-row">
          <div class="col-sub-project">
            <router-link
              :to="planDetailLink(tp)"
              class="pm-sub-link"
            >
              {{ tp.subProjectName || '—' }}
            </router-link>
            <div v-if="tp.qaOwners && tp.qaOwners.length > 0" class="qa-owner-wrap">
              <span
                v-for="owner in tp.qaOwners.slice(0, 3)"
                :key="owner.userId"
                class="qa-owner-name bo-assignee-pill"
              >
                {{ formatAssignedName(owner.fullName) }}
              </span>
              <span v-if="tp.qaOwners.length > 3" class="qa-owner-more">
                +{{ tp.qaOwners.length - 3 }}
              </span>
            </div>
          </div>
          <div class="col-project">
            <span class="pm-cell-secondary">{{ tp.projectName || '—' }}</span>
          </div>
          <div v-if="isHistoryMode" class="col-team text-center">
            <span class="pm-cell-secondary">{{ teamLabelForPlan(tp) }}</span>
          </div>
          <div class="col-sprint text-center">
            <div v-if="planSprintIds(tp).length" class="pm-sprint-cell">
              <span class="pm-sprint-current">{{ sprintShortLabelById(planLatestSprintId(tp)) }}</span>
              <button
                v-if="planPriorSprintIds(tp).length"
                type="button"
                class="pm-sprint-prior-btn"
                @click.stop="toggleSprintHistoryPopup(tp)"
              >
                Sprint Parent
              </button>
              <div
                v-if="sprintHistoryOpenPlanId === tp.testPlanId"
                class="pm-sprint-popup pm-sprint-popup--open"
                @click.stop
              >
                <p class="pm-sprint-popup-title">Sprint Parent</p>
                <ul class="pm-sprint-popup-list">
                  <li
                    v-for="item in planSprintPopupItems(tp)"
                    :key="item.id"
                    class="pm-sprint-popup-item"
                    :class="{ 'pm-sprint-popup-item--current': item.isCurrent }"
                  >
                    <span class="pm-sprint-popup-label">{{ item.label }}</span>
                    <span v-if="item.isCurrent" class="pm-sprint-popup-now">current</span>
                    <span v-if="item.detail" class="pm-sprint-popup-detail">{{ item.detail }}</span>
                  </li>
                </ul>
              </div>
            </div>
            <span v-else class="pm-cell-secondary">—</span>
          </div>
          <div class="col-date text-center">
            <span class="pm-cell-secondary">{{ formatDate(tp.planOverallStart) }}</span>
          </div>
          <div class="col-date text-center">
            <span class="pm-cell-secondary">{{ formatDate(tp.planOverallDue) }}</span>
          </div>
          <div class="col-status text-center">
            <span class="status-pill" :class="statusPillClass(tp.testPlanStatus)">
              {{ statusLabel(tp.testPlanStatus) }}
            </span>
          </div>
          <div v-if="!isHistoryMode" class="col-action text-center">
            <button class="btn btn-link btn-sm p-0 action-edit me-2" title="Edit" @click="openEdit(tp)">
              <i class="fas fa-edit" style="font-size:0.85rem;"></i>
            </button>
            <button class="btn btn-link btn-sm p-0 action-delete" title="Delete" @click="remove(tp)">
              <i class="fas fa-trash-alt" style="font-size:0.85rem;"></i>
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL, fetchWithTimeout } from "@/config/apiBase";
import {
  projectsApi,
  subProjectsApi,
  sprintSettingsApi,
  testPlansApi,
  usersApi,
} from "@/services/backendApi";
import {
  statusLabel,
  statusPillClass,
  normalizeSubProjectStatus,
  isPlanHistoryStatus,
  PLAN_MANAGEMENT_STATUS_OPTIONS,
} from "@/utils/subProjectStatus";
import JiraLinksPanel from "@/components/JiraLinksPanel.vue";

export default {
  name: "test-plan-table",
  components: { JiraLinksPanel },
  props: {
    teamFilter: {
      type: String,
      default: "",
    },
    historyMode: {
      type: Boolean,
      default: false,
    },
    boardMode: {
      type: String,
      default: "",
    },
    lockTeamFilter: {
      type: Boolean,
      default: false,
    },
    sprints: {
      // Full list of sprints across all teams, supplied by parent.
      type: Array,
      default: () => [],
    },
    detailTab: { type: String, default: "" },
    detailFrom: { type: String, default: "team" },
    detailTeam: { type: String, default: "" },
    detailBoard: { type: String, default: "" },
  },
  data() {
    return {
      loading: false,
      showModal: false,
      showQaDropdown: false,
      showDevDropdown: false,
      showSprintDropdown: false,
      editId: null,
      editProjectPlanId: null,
      form: { projectId: "", subProjectId: "", planOverallStart: "", planOverallDue: "", qaOwnerIds: [], devOwnerIds: [], pmOwnerId: "", scope: "", status: "to_do", jiraProjectKey: "", jiraIssueKeys: [], sprintIds: [] },
      testPlans: [],
      projects: [],
      allSubProjects: [],
      users: [],
      jiraProjects: [],
      jiraIssues: [],
      jiraLoadingProjects: false,
      jiraLoadingIssues: false,
      jiraSearch: "",
      jiraSearchTimer: null,
      saveError: null,
      saving: false,
      loadedSprints: [],
      listSprintFilter: null,
      historyTeamFilter: null,
      sprintHistoryOpenPlanId: null,
    };
  },
  watch: {
    teamFilter() {
      this.applyDefaultListSprintFilter();
    },
    historyMode(val) {
      if (val) this.historyTeamFilter = null;
    },
  },
  computed: {
    isHistoryMode() {
      return this.boardMode === "history"
        || this.historyMode
        || this.normalizeTeamValue(this.teamFilter) === "history";
    },
    isBacklogMode() {
      return this.boardMode === "backlog";
    },
    isAssignMode() {
      return this.boardMode === "assign";
    },
    isReadyMode() {
      return this.boardMode === "ready";
    },
    emptyPlansMessage() {
      if (this.isHistoryMode) return "No archived plans yet.";
      if (this.isBacklogMode) return "No backlog plans yet. Click \"Create Plan\" to add one.";
      if (this.isReadyMode) return "No plans ready to deploy yet.";
      if (this.isAssignMode) return "No plans to assign yet. Click \"Create Plan\" to add one.";
      return "No test plans yet. Click \"Create Plan\" to add one.";
    },
    allSprints() {
      return Array.isArray(this.sprints) && this.sprints.length
        ? this.sprints
        : this.loadedSprints;
    },
    currentUserId() {
      const raw = this.$store?.state?.user?.userId;
      return raw != null ? Number(raw) : null;
    },
    filteredSubProjects() {
      if (!this.form.projectId) return [];

      const usedSubProjectIds = new Set();
      const deployedSubProjectIds = new Set();
      for (const plan of this.testPlans || []) {
        const spId = Number(plan.subProjectId);
        if (!Number.isFinite(spId)) continue;
        usedSubProjectIds.add(spId);
        const planStatus = normalizeSubProjectStatus(plan.testPlanStatus);
        if (isPlanHistoryStatus(planStatus)) {
          deployedSubProjectIds.add(spId);
        }
      }

      return this.allSubProjects
        .filter((s) => Number(s.projectId) === Number(this.form.projectId))
        .filter((s) => {
          const spId = Number(s.subProjectId);
          const subStatus = normalizeSubProjectStatus(s.subProjectStatus);
          if (subStatus === "deployed") return false;
          if (deployedSubProjectIds.has(spId)) return false;
          return true;
        })
        .map((s) => ({
          ...s,
          disabled: usedSubProjectIds.has(Number(s.subProjectId)),
        }));
    },
    displayedTestPlans() {
      const teamBySubProjectId = new Map(
        (this.allSubProjects || []).map((sub) => [
          Number(sub.subProjectId),
          this.normalizeTeamValue(sub.teamTrimmed || sub.team),
        ]),
      );

      const matchesTeam = (plan) => {
        const normalizedFilter = this.normalizeTeamValue(this.teamFilter);
        if (!normalizedFilter || normalizedFilter === "history") return true;
        const team = teamBySubProjectId.get(Number(plan.subProjectId))
          || this.normalizeTeamValue(plan.teamTrimmed)
          || "unassign";
        return team === normalizedFilter;
      };

      if (this.isHistoryMode) {
        const historyTeam = this.lockTeamFilter
          ? this.normalizeTeamValue(this.teamFilter)
          : (this.historyTeamFilter ? this.normalizeTeamValue(this.historyTeamFilter) : "");
        return this.testPlans.filter((plan) => {
          const status = normalizeSubProjectStatus(plan.testPlanStatus);
          if (!isPlanHistoryStatus(status)) return false;
          if (!historyTeam) return true;
          const team = teamBySubProjectId.get(Number(plan.subProjectId))
            || this.normalizeTeamValue(plan.teamTrimmed)
            || "unassign";
          return team === historyTeam;
        });
      }

      if (this.isReadyMode) {
        return this.testPlans.filter((plan) => {
          const status = normalizeSubProjectStatus(plan.testPlanStatus);
          return status === "ready_to_deploy" && matchesTeam(plan);
        });
      }

      if (this.isAssignMode) {
        return this.testPlans.filter((plan) => {
          const status = normalizeSubProjectStatus(plan.testPlanStatus);
          if (isPlanHistoryStatus(status)) return false;
          return matchesTeam(plan);
        });
      }

      const sprintFilterId =
        this.listSprintFilter !== null &&
        this.listSprintFilter !== "" &&
        this.listSprintFilter !== undefined
          ? Number(this.listSprintFilter)
          : null;

      return this.testPlans.filter((plan) => {
        const status = normalizeSubProjectStatus(plan.testPlanStatus);
        if (isPlanHistoryStatus(status)) return false;
        if (!matchesTeam(plan)) return false;
        const ids = this.planSprintIds(plan);
        if (this.isBacklogMode) return ids.length === 0;
        if (this.boardMode === "current" && !ids.length) return false;
        if (sprintFilterId !== null && !ids.includes(sprintFilterId)) return false;
        return true;
      });
    },
    historyTeamFilterOptions() {
      return [
        { value: null, label: "All teams" },
        { value: "team a", label: "Team A" },
        { value: "team b", label: "Team B" },
        { value: "unassign", label: "Team C" },
      ];
    },
    createTeamCode() {
      if (this.isHistoryMode) return null;
      const key = this.normalizeTeamValue(this.teamFilter);
      if (key === "team a") return "A";
      if (key === "team b") return "B";
      if (key === "unassign" || key === "other" || key === "c") return "C";
      return null;
    },
    /**
     * Sprints that should appear in the create/edit modal's Sprint dropdown.
     * Filtered to match the active team tab so users only see their team's sprints.
     */
    availableSprintsForForm() {
      const teamKey = this.teamKeyForFilter(this.teamFilter);
      const list = this.allSprints;
      if (!teamKey) return list;
      return list.filter(
        (s) => String(s.team || "").trim().toLowerCase() === teamKey
      );
    },
    listSprintFilterOptions() {
      const options = [{ value: null, label: "All" }];
      this.availableSprintsForForm.forEach((s) => {
        options.push({
          value: Number(s.sprintId),
          label: this.sprintOptionLabel(s),
        });
      });
      return options;
    },
    /**
     * Plain list of {value,label} pairs for the Test Plan status dropdown.
     * Keeping the option set in one place keeps the markup tidy now that
     * we use <base-select> instead of inline <option> elements.
     */
    testPlanStatusOptions() {
      const base = [...PLAN_MANAGEMENT_STATUS_OPTIONS];
      const cur = normalizeSubProjectStatus(this.form.status);
      if (cur && !base.some((o) => o.value === cur)) {
        base.push({ value: cur, label: statusLabel(cur) });
      }
      return base;
    },
    planJiraPanelLinks() {
      return (this.form.jiraIssueKeys || []).map((key) => ({
        key,
        url: `https://lightnet-thailand.atlassian.net/browse/${key}`,
      }));
    },
  },

  async created() {
    await Promise.all([
      this.loadTestPlans(),
      this.loadProjects(),
      this.loadSubProjects(),
      this.loadUsers(),
      this.loadSprintsIfNeeded(),
    ]);
    this.applyDefaultListSprintFilter();
    document.addEventListener("click", this.closeQaDropdown);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeQaDropdown);
  },
  methods: {
    planDetailLink(tp) {
      const query = { from: this.detailFrom || "team" };
      if (this.detailTab) query.tab = this.detailTab;
      if (this.detailTeam) query.team = this.detailTeam;
      if (this.detailBoard) query.board = this.detailBoard;
      return {
        name: "Test Plan Detail",
        params: { id: tp.projectPlanId },
        query,
      };
    },
    normalizeTeamValue(rawTeam) {
      const key = String(rawTeam || "").trim().toLowerCase();
      if (key === "history") return "history";
      if (key === "all") return "";
      if (!key || key === "-" || key === "n/a" || key === "null" || key === "undefined") return "unassign";
      if (key === "a" || key === "teama" || key === "team a") return "team a";
      if (key === "b" || key === "teamb" || key === "team b") return "team b";
      if (key === "c" || key === "teamc" || key === "team c") return "unassign";
      if (key === "unassign" || key === "unassigned") return "unassign";
      if (key === "other") return "unassign";
      return key;
    },
    /** Convert plan tab filter to sprint_setup.team ("a" / "b" / "c"). */
    teamKeyForFilter(rawTeam) {
      const normalized = this.normalizeTeamValue(rawTeam);
      if (normalized === "team a") return "a";
      if (normalized === "team b") return "b";
      if (normalized === "unassign") return "c";
      return "";
    },
    sprintOptionLabel(s) {
      const round =
        s.sprintRound != null && s.sprintRound !== ""
          ? `Sprint ${s.sprintRound}`
          : "Sprint";
      const name = s.sprintName ? ` — ${s.sprintName}` : "";
      const start = this.formatSprintDate(s.startDate);
      const end = this.formatSprintDate(s.endDate);
      const range = start && end ? ` (${start} – ${end})` : "";
      return `${round}${name}${range}`;
    },
    sprintLabelForPlan(plan) {
      const id = plan?.sprintId;
      if (id == null || id === "") return "—";
      const s = this.allSprints.find((x) => Number(x.sprintId) === Number(id));
      if (!s) return "—";
      if (s.sprintRound != null && s.sprintRound !== "") {
        return s.sprintName ? `Sprint ${s.sprintRound}: ${s.sprintName}` : `Sprint ${s.sprintRound}`;
      }
      return s.sprintName || "—";
    },
    async loadSprintsIfNeeded() {
      if (Array.isArray(this.sprints) && this.sprints.length) return;
      try {
        this.loadedSprints = await sprintSettingsApi.getAll();
      } catch (e) {
        console.error("loadSprintsIfNeeded error:", e);
        this.loadedSprints = [];
      }
    },
    formatSprintDate(val) {
      if (!val) return "";
      const s = String(val).substring(0, 10);
      const [y, m, d] = s.split("-");
      if (!y || !m || !d) return "";
      return `${d}/${m}/${y}`;
    },
    /**
     * Pick the sprint that contains today's date for the currently active team.
     * Used as the default value when opening "Create Plan".
     *
     * Example: if Sprint 1 is 18–22 May and today is 19 May → returns Sprint 1.
     *          if today is 25 May → returns whichever sprint covers 25 May.
     *          if no sprint covers today → returns null.
     */
    findCurrentSprintId() {
      const list = this.availableSprintsForForm;
      if (!list.length) return null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const match = list.find((s) => {
        if (!s.startDate || !s.endDate) return false;
        const start = new Date(`${String(s.startDate).substring(0, 10)}T00:00:00`);
        const end = new Date(`${String(s.endDate).substring(0, 10)}T00:00:00`);
        end.setHours(23, 59, 59, 999);
        return today >= start && today <= end;
      });
      return match ? Number(match.sprintId) : null;
    },
    /** Latest sprint for the active team (most recent start_date / highest round). */
    findLatestSprintId() {
      const list = this.availableSprintsForForm;
      if (!list.length) return null;
      const sorted = [...list].sort((a, b) => {
        const dA = String(a.startDate || "").substring(0, 10);
        const dB = String(b.startDate || "").substring(0, 10);
        if (dB !== dA) return dB.localeCompare(dA);
        return (Number(b.sprintRound) || 0) - (Number(a.sprintRound) || 0);
      });
      return Number(sorted[0].sprintId);
    },
    applyDefaultListSprintFilter() {
      this.listSprintFilter = this.findCurrentSprintId();
    },
    onSprintSelectChange(event) {
      const raw = event.target.value;
      const sid = raw === "" ? null : Number(raw);
      this.form.sprintIds = sid == null ? [] : [sid];
    },
    async loadTestPlans() {
      this.loading = true;
      try { this.testPlans = await testPlansApi.getAll(); }
      catch (e) { console.error("loadTestPlans error:", e); }
      finally { this.loading = false; }
    },
    async loadProjects() {
      try { this.projects = await projectsApi.getAll(); } catch (e) { console.error(e); }
    },
    async loadSubProjects() {
      try {
        const uid = this.$store?.state?.user?.userId;
        const params = uid ? { userId: uid } : undefined;
        this.allSubProjects = await subProjectsApi.getAll(params);
      } catch (e) { console.error(e); }
    },
    async loadUsers() {
      try { this.users = await usersApi.getAll(); } catch (e) { console.error(e); }
    },

    closeQaDropdown(e) {
      if (this.$refs.qaDropdownRef && !this.$refs.qaDropdownRef.contains(e.target)) {
        this.showQaDropdown = false;
      }
      if (this.$refs.devDropdownRef && !this.$refs.devDropdownRef.contains(e.target)) {
        this.showDevDropdown = false;
      }
      if (this.$refs.sprintDropdownRef && !this.$refs.sprintDropdownRef.contains(e.target)) {
        this.showSprintDropdown = false;
      }
      if (!e.target?.closest?.(".pm-sprint-cell")) {
        this.sprintHistoryOpenPlanId = null;
      }
    },
    toggleSprintHistoryPopup(tp) {
      const id = tp?.testPlanId;
      this.sprintHistoryOpenPlanId = this.sprintHistoryOpenPlanId === id ? null : id;
    },
    planSprintIds(plan) {
      if (Array.isArray(plan?.sprintIds) && plan.sprintIds.length) {
        return plan.sprintIds.map((v) => Number(v)).filter((n) => Number.isFinite(n));
      }
      if (plan?.sprintId != null && plan.sprintId !== "") return [Number(plan.sprintId)];
      return [];
    },
    sprintShortLabelById(id) {
      const s = this.allSprints.find((x) => Number(x.sprintId) === Number(id));
      if (!s) return `Sprint #${id}`;
      if (s.sprintRound != null && s.sprintRound !== "") {
        return s.sprintName ? `Sprint ${s.sprintRound}` : `Sprint ${s.sprintRound}`;
      }
      return s.sprintName || `Sprint #${id}`;
    },
    planSprintEntriesSorted(plan) {
      const ids = this.planSprintIds(plan);
      if (!ids.length) return [];
      return ids
        .map((id) => {
          const s = this.allSprints.find((x) => Number(x.sprintId) === Number(id));
          const round = Number(s?.sprintRound);
          const startMs = s?.startDate
            ? new Date(`${String(s.startDate).slice(0, 10)}T00:00:00Z`).getTime()
            : 0;
          return {
            id: Number(id),
            round: Number.isFinite(round) ? round : 0,
            startMs: Number.isFinite(startMs) ? startMs : 0,
          };
        })
        .sort((a, b) => {
          if (a.round !== b.round) return a.round - b.round;
          if (a.startMs !== b.startMs) return a.startMs - b.startMs;
          return a.id - b.id;
        });
    },
    planLatestSprintId(plan) {
      const entries = this.planSprintEntriesSorted(plan);
      return entries.length ? entries[entries.length - 1].id : null;
    },
    planPriorSprintIds(plan) {
      const entries = this.planSprintEntriesSorted(plan);
      return entries.length > 1 ? entries.slice(0, -1).map((e) => e.id) : [];
    },
    planSprintPopupItems(plan) {
      const entries = this.planSprintEntriesSorted(plan);
      const latestId = this.planLatestSprintId(plan);
      return [...entries].reverse().map((e) => {
        const s = this.allSprints.find((x) => Number(x.sprintId) === Number(e.id));
        const detail = s ? this.sprintPopupDetail(s) : null;
        return {
          id: e.id,
          label: this.sprintShortLabelById(e.id),
          detail,
          isCurrent: e.id === latestId,
        };
      });
    },
    sprintPopupDetail(s) {
      const name = s.sprintName ? String(s.sprintName).trim() : "";
      const start = this.formatSprintDate(s.startDate);
      const end = this.formatSprintDate(s.endDate);
      if (name && start && end) return `${name} · ${start}–${end}`;
      if (name) return name;
      if (start && end) return `${start}–${end}`;
      return "";
    },
    toggleSprint(id) {
      const sid = Number(id);
      const idx = this.form.sprintIds.indexOf(sid);
      if (idx === -1) this.form.sprintIds.push(sid);
      else this.form.sprintIds.splice(idx, 1);
    },
    removeSprint(id) {
      const sid = Number(id);
      this.form.sprintIds = this.form.sprintIds.filter((v) => v !== sid);
    },
    toggleQaOwner(uid) {
      const idx = this.form.qaOwnerIds.indexOf(uid);
      if (idx === -1) this.form.qaOwnerIds.push(uid);
      else this.form.qaOwnerIds.splice(idx, 1);
    },
    removeQaOwner(uid) {
      this.form.qaOwnerIds = this.form.qaOwnerIds.filter(id => id !== uid);
    },
    toggleDevOwner(uid) {
      const idx = this.form.devOwnerIds.indexOf(uid);
      if (idx === -1) this.form.devOwnerIds.push(uid);
      else this.form.devOwnerIds.splice(idx, 1);
    },
    removeDevOwner(uid) {
      this.form.devOwnerIds = this.form.devOwnerIds.filter(id => id !== uid);
    },
    userNameById(uid) {
      const u = this.users.find(u => Number(u.userId) === uid);
      return u ? this.formatUserDisplayName(u) : uid;
    },
    formatUserDisplayName(user) {
      const first = String(user?.firstName || "").trim();
      const nick = String(user?.nickname || "").trim();
      if (!first) return "—";
      return nick ? `${first} (${nick})` : first;
    },
    formatAssignedName(rawName) {
      const name = String(rawName || "").trim();
      if (!name) return "—";
      const nicknameMatch = name.match(/\(([^)]+)\)/);
      const firstName = name.replace(/\([^)]*\)/g, " ").trim().split(/\s+/)[0] || "";
      if (nicknameMatch && nicknameMatch[1]) {
        return `${firstName} (${nicknameMatch[1].trim()})`;
      }
      return firstName;
    },
    teamLabelForPlan(plan) {
      const teamBySubProjectId = new Map(
        (this.allSubProjects || []).map((sub) => [
          Number(sub.subProjectId),
          this.normalizeTeamValue(sub.teamTrimmed || sub.team),
        ]),
      );
      const team = teamBySubProjectId.get(Number(plan.subProjectId))
        || this.normalizeTeamValue(plan.teamTrimmed)
        || "unassign";
      if (team === "team a") return "Team A";
      if (team === "team b") return "Team B";
      if (team === "unassign" || team === "other" || team === "c") return "Team C";
      return team || "—";
    },

    onProjectChange() {
      this.form.subProjectId = "";
      this.form.jiraProjectKey = "";
      this.form.jiraIssueKeys = [];
      this.jiraIssues = [];
    },
    onSubProjectChange() {
      this.form.jiraIssueKeys = [];
      this.form.jiraProjectKey = "";
      this.jiraIssues = [];
      if (!this.form.subProjectId) return;
      const sub = this.allSubProjects.find(s => Number(s.subProjectId) === Number(this.form.subProjectId));
      if (!sub || !sub.projectLink) return;
      const links = this.parseJiraLinks(sub.projectLink);
      if (links.length === 0) return;
      const keys = links.map(l => l.key);
      const projectKey = keys[0].split('-')[0];
      this.form.jiraIssueKeys = keys;
      this.form.jiraProjectKey = projectKey;
      this.loadJiraIssues(projectKey);
    },
    async openCreate() {
      this.editId = null;
      this.editProjectPlanId = null;
      this.showQaDropdown = false;
      this.showDevDropdown = false;
      this.showSprintDropdown = false;
      this.jiraSearch = "";
      this.jiraIssues = [];
      this.saveError = null;
      const defaultSprintId = this.findLatestSprintId();
      this.form = {
        projectId: "",
        subProjectId: "",
        planOverallStart: "",
        planOverallDue: "",
        qaOwnerIds: [],
        devOwnerIds: [],
        pmOwnerId: "",
        scope: "",
        status: "to_do",
        jiraProjectKey: "",
        jiraIssueKeys: [],
        sprintIds: defaultSprintId != null ? [defaultSprintId] : [],
      };
      this.showModal = true;
      try {
        await Promise.all([
          this.loadProjects(),
          this.loadSubProjects(),
          this.loadUsers(),
          this.loadSprintsIfNeeded(),
        ]);
      } catch (_) {
        // Keep modal open even if one preload call fails.
      }
      this.loadJiraProjects();
    },
    async openEdit(tp) {
      this.editId = tp.testPlanId;
      this.editProjectPlanId = tp.projectPlanId;
      this.showQaDropdown = false;
      this.showDevDropdown = false;
      this.showSprintDropdown = false;
      this.form = {
        projectId: Number(tp.projectId),
        subProjectId: Number(tp.subProjectId),
        testPlanName: tp.testPlanName,
        planOverallStart: tp.planOverallStart ? String(tp.planOverallStart).substring(0, 10) : "",
        planOverallDue: tp.planOverallDue ? String(tp.planOverallDue).substring(0, 10) : "",
        qaOwnerIds: (tp.qaOwners || []).map(o => Number(o.userId)),
        devOwnerIds: (tp.devOwners || []).map(o => Number(o.userId)),
        pmOwnerId: (tp.pmOwners && tp.pmOwners.length > 0) ? Number(tp.pmOwners[0].userId) : "",
        scope: tp.scope || "",
        status: normalizeSubProjectStatus(tp.testPlanStatus) || "to_do",
        jiraIssueKeys: this.parseJiraLinks(tp.projectLink).map(l => l.key),
        jiraProjectKey: this.parseJiraLinks(tp.projectLink)[0]?.key.split('-')[0] || "",
        sprintIds: this.planSprintIds(tp),
      };
      this.jiraSearch = "";
      this.jiraIssues = [];
      this.saveError = null;
      this.showModal = true;
      try {
        await Promise.all([
          this.loadProjects(),
          this.loadSubProjects(),
          this.loadUsers(),
          this.loadSprintsIfNeeded(),
        ]);
      } catch (_) {
        // Keep modal open even if one preload call fails.
      }
      this.loadJiraProjects();
      if (this.form.jiraProjectKey) this.loadJiraIssues(this.form.jiraProjectKey);
    },
    closeModal() {
      this.showModal = false;
      this.showQaDropdown = false;
      this.showDevDropdown = false;
      this.showSprintDropdown = false;
    },

    formatDate(val) {
      if (!val) return "—";
      const s = String(val).substring(0, 10);
      const [y, m, d] = s.split("-");
      return `${d}-${m}-${y}`;
    },
    statusLabel,
    statusPillClass,

    async save() {
      if (this.saving) return;
      if (!this.editId && (!this.form.projectId || !this.form.subProjectId)) return;
      if (!this.editId) {
        const picked = this.filteredSubProjects.find(
          (s) => Number(s.subProjectId) === Number(this.form.subProjectId),
        );
        if (picked?.disabled) {
          this.saveError = "This sub project already has a plan.";
          return;
        }
      }
      this.saveError = null;
      this.saving = true;
      try {
        if (this.editId) {
          const base = 'https://lightnet-thailand.atlassian.net/browse';
          const keys = this.form.jiraIssueKeys || [];
          const jiraUrl = keys.length > 0
            ? JSON.stringify(keys.map(k => ({ key: k, url: `${base}/${k}` })))
            : null;
          console.log('[save edit] jiraIssueKeys:', keys, 'jiraUrl:', jiraUrl);
          await testPlansApi.update(this.editId, {
            testPlanName: this.form.testPlanName,
            planOverallStart: this.form.planOverallStart || null,
            planOverallDue: this.form.planOverallDue || null,
            status: normalizeSubProjectStatus(this.form.status) || "to_do",
            qaOwnerIds: this.form.qaOwnerIds,
            devOwnerIds: this.form.devOwnerIds,
            pmOwnerIds: this.form.pmOwnerId ? [Number(this.form.pmOwnerId)] : [],
            scope: this.form.scope || null,
            projectPlanId: this.editProjectPlanId,
            subProjectId: this.form.subProjectId,
            jiraUrl,
            actorUserId: this.currentUserId,
            sprintIds: [...this.form.sprintIds],
          });
        } else {
          const sub = this.allSubProjects.find(s => Number(s.subProjectId) === Number(this.form.subProjectId));
          await testPlansApi.insert({
            subProjectId: this.form.subProjectId,
            testPlanName: sub ? sub.subProjectName : "Test Plan",
            planOverallStart: this.form.planOverallStart || null,
            planOverallDue: this.form.planOverallDue || null,
            qaOwnerIds: this.form.qaOwnerIds,
            devOwnerIds: this.form.devOwnerIds,
            pmOwnerIds: this.form.pmOwnerId ? [Number(this.form.pmOwnerId)] : [],
            scope: this.form.scope || null,
            status: normalizeSubProjectStatus(this.form.status) || "to_do",
            createJiraSubtask: (this.form.jiraIssueKeys || []).length > 0,
            jiraIssueKeys: this.form.jiraIssueKeys || [],
            appBaseUrl: window.location.origin,
            actorUserId: this.currentUserId,
            team: this.createTeamCode,
            sprintIds: [...this.form.sprintIds],
          });
        }
        await this.loadTestPlans();
        this.closeModal();
      } catch (e) {
        console.error("save test plan error:", e);
        this.saveError = e.message || "Save failed. Please try again.";
      } finally {
        this.saving = false;
      }
    },

    parseJiraLinks(raw) {
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      } catch (_) { /* ignore parse errors */ }
      const m = raw.match(/\/browse\/([A-Z]+-\d+)/);
      if (m) return [{ key: m[1], url: raw }];
      return [];
    },

    async loadJiraProjects() {
      if (this.jiraProjects.length > 0) return;
      this.jiraLoadingProjects = true;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/projects`);
        this.jiraProjects = await res.json();
      } catch (e) { console.error("loadJiraProjects error:", e); }
      finally { this.jiraLoadingProjects = false; }
    },
    async loadJiraIssues(projectKey, search = "") {
      this.jiraLoadingIssues = true;
      try {
        const q = search ? `&search=${encodeURIComponent(search)}` : "";
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/issues?projectKey=${projectKey}${q}`);
        this.jiraIssues = await res.json();
      } catch (e) { console.error("loadJiraIssues error:", e); }
      finally { this.jiraLoadingIssues = false; }
    },
    onJiraProjectChange() {
      this.form.jiraIssueKeys = [];
      this.jiraIssues = [];
      this.jiraSearch = "";
      if (this.form.jiraProjectKey) this.loadJiraIssues(this.form.jiraProjectKey);
    },
    toggleJiraTicket(key) {
      const idx = this.form.jiraIssueKeys.indexOf(key);
      if (idx === -1) this.form.jiraIssueKeys.push(key);
      else this.form.jiraIssueKeys.splice(idx, 1);
      console.log('[toggleJiraTicket] after toggle:', JSON.stringify(this.form.jiraIssueKeys));
    },
    removeJiraTicket(key) {
      this.form.jiraIssueKeys = this.form.jiraIssueKeys.filter(k => k !== key);
    },
    onJiraSearch() {
      clearTimeout(this.jiraSearchTimer);
      this.jiraSearchTimer = setTimeout(() => {
        if (this.form.jiraProjectKey) this.loadJiraIssues(this.form.jiraProjectKey, this.jiraSearch);
      }, 400);
    },

    async remove(tp) {
      if (!confirm(`Delete "${tp.testPlanName}"?`)) return;
      try {
        await testPlansApi.delete(tp.testPlanId);
        await this.loadTestPlans();
      } catch (e) {
        console.error("delete test plan error:", e);
        alert(e.message || "Delete failed");
      }
    },
  },
};
</script>

<style scoped>
.table-create-btn {
  font-size: 0.8rem;
}

.plan-mgmt-inset {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  padding: 1rem;
}

.plan-mgmt-inset > .pm-tab-table-wrap {
  min-width: 0;
  width: 100%;
}

.plan-table-toolbar-actions {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.sprint-list-filter {
  min-width: 220px;
}

.plan-mgmt-inset > .pm-tab-table-wrap > .pm-table.pm-tab-table {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.plan-mgmt-inset > .pm-tab-table-wrap .pm-table-header {
  display: flex;
  align-items: center;
  padding: 0.65rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.7rem;
  font-weight: 700;
  color: #8392ab;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.plan-mgmt-inset > .pm-tab-table-wrap .pm-row {
  display: flex;
  align-items: flex-start;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #f1f3f5;
  transition: background 0.15s;
}

.plan-mgmt-inset > .pm-tab-table-wrap .pm-row:last-child {
  border-bottom: none;
}

.plan-mgmt-inset > .pm-tab-table-wrap .pm-row:hover {
  background: #fafbfc;
}

.col-sub-project {
  flex: 4.05;
  min-width: 0;
}

.col-project {
  flex: 1.25;
  min-width: 0;
}

.col-date {
  flex: 1.2;
  min-width: 92px;
}

.col-sprint {
  flex: 1.6;
  min-width: 110px;
  position: relative;
  overflow: visible;
}

.col-team {
  flex: 1;
  min-width: 80px;
}

.col-status {
  flex: 1.15;
  min-width: 100px;
}

.col-action {
  flex: 0.9;
  min-width: 72px;
}

.col-project,
.col-date,
.col-sprint,
.col-team,
.col-status,
.col-action {
  padding-top: 0.12rem;
}

.pm-sub-link {
  display: block;
  font-size: 0.875rem;
  font-weight: 700;
  color: #344767;
  text-decoration: none;
}

.pm-sub-link:hover {
  color: #5e72e4;
}

.pm-cell-secondary {
  font-size: 0.8rem;
  color: #67748e;
}

.pm-sprint-cell {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
  max-width: 100%;
}

.pm-sprint-current {
  font-size: 0.8rem;
  font-weight: 600;
  color: #344767;
  line-height: 1.25;
}

.pm-sprint-prior-btn {
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  font-size: 0.68rem;
  color: #8392ab;
  line-height: 1.2;
  white-space: normal;
  cursor: pointer;
}

.pm-sprint-prior-btn:hover {
  color: #67748e;
  text-decoration: underline;
}

.pm-sprint-popup {
  position: absolute;
  left: 50%;
  top: calc(100% + 6px);
  z-index: 3000;
  min-width: 168px;
  max-width: 240px;
  padding: 0.45rem 0.55rem;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(52, 71, 103, 0.12);
  text-align: left;
  opacity: 0;
  visibility: hidden;
  transform: translateX(-50%) translateY(4px);
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s;
  pointer-events: none;
}

.pm-sprint-popup--open {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
  pointer-events: auto;
}

.pm-sprint-popup-title {
  margin: 0 0 0.35rem;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8392ab;
}

.pm-sprint-popup-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.pm-sprint-popup-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.2rem 0.35rem;
  padding: 0.28rem 0;
  border-bottom: 1px solid #f1f3f5;
}

.pm-sprint-popup-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.pm-sprint-popup-item--current .pm-sprint-popup-label {
  color: #344767;
  font-weight: 700;
}

.pm-sprint-popup-label {
  font-size: 0.76rem;
  color: #67748e;
  font-weight: 600;
}

.pm-sprint-popup-now {
  font-size: 0.58rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #5e72e4;
  background: #f3efff;
  border-radius: 999px;
  padding: 0.04rem 0.34rem;
}

.pm-sprint-popup-detail {
  flex: 1 1 100%;
  font-size: 0.64rem;
  color: #8392ab;
  line-height: 1.25;
}

.pm-sprint-list {
  display: block;
  font-size: 0.78rem;
  line-height: 1.35;
  white-space: normal;
}

.qa-owner-wrap {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.28rem;
  margin-top: 0.28rem;
}

.qa-owner-name {
  font-size: 0.7rem;
  color: #67748e;
  background: #f3f5f7;
  border: 1px solid #e7eaef;
  border-radius: 999px;
  padding: 0.06rem 0.42rem;
}

.bo-assignee-pill {
  background: #e8f4fd !important;
  color: #0c7a9a !important;
  border-color: #bee5eb !important;
}

.qa-owner-more {
  font-size: 0.7rem;
  color: #8392ab;
}

.owner-badge {
  display: inline-block;
  background: #e8f4fd;
  color: #0c7a9a;
  border: 1px solid #bee5eb;
  border-radius: 50px;
  font-size: 0.7rem;
  font-weight: 500;
  padding: 0.04rem 0.26rem;
  line-height: 1.05;
}
.owner-badge-muted {
  background: #f2f4f7;
  color: #6b7280;
  border-color: #d9dee6;
}
.action-edit   { color: #4361ee; }
.action-edit:hover { color: #2d4bc8; }
.action-delete { color: #e74c3c; }
.action-delete:hover { color: #c0392b; }

.jira-link-pill {
  display: inline-flex;
  align-items: center;
  background: #e8f4fd;
  color: #0c7a9a;
  border: 1px solid #bee5eb;
  border-radius: 50px;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 7px;
  text-decoration: none;
  white-space: nowrap;
}
.jira-link-pill:hover { background: #d1ecf1; color: #0a6c87; }

.jira-tag {
  display: inline-flex;
  align-items: center;
  background: #e8f4fd;
  color: #0c7a9a;
  border: 1px solid #bee5eb;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 8px;
  margin: 2px 2px 2px 0;
}
.jira-tag-remove {
  background: none;
  border: none;
  color: #0c7a9a;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0 0 0 4px;
  line-height: 1;
}
.jira-issue-list {
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}
.jira-issue-item {
  padding: 6px 10px;
  cursor: pointer;
  font-size: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-box {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
}
.plan-modal-box {
  display: flex;
  flex-direction: column;
}
.plan-modal-box--split {
  overflow: hidden;
}
.plan-modal-split-body {
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.plan-modal-pane-form {
  flex: 1 1 50%;
  width: 50%;
  max-width: 50%;
  min-width: 0;
  overflow-y: auto;
  overflow-x: visible;
  max-height: 65vh;
  border-right: 1px solid #e9ecef;
}
.plan-modal-pane-form :deep(.base-select--open) {
  position: relative;
  z-index: 20;
}
.plan-modal-pane-jira {
  flex: 1 1 50%;
  width: 50%;
  max-width: 50%;
  min-width: 0;
  padding: 1rem;
  overflow: hidden;
  max-height: 65vh;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-left: 1px solid #e9ecef;
}
.plan-modal-pane-jira :deep(.jira-links-embedded) {
  flex: 1;
  min-height: 0;
}
.jira-issue-list--plain .jira-issue-item.jira-issue-selected {
  background: transparent;
}
.jira-issue-item:last-child { border-bottom: none; }
.jira-issue-item:hover { background: #f8f9fa; }
.jira-issue-selected { background: #e8f4fd; }
</style>

